import {
  ActivateSubscriptionController,
  ActiveShareholderRegisterSubscriptionContext,
  orderUpgrade,
  SubscriptionTypeSelectionController,
} from "./Payment";
import { observer } from "mobx-react";
import { FC, Fragment } from "react";
import { Box, Grid, GridProps, VBox } from "../components-v2/Box";
import { MSelect } from "../components-v2/Select";
import { MRadioButtonGroup } from "../components-v2/RadioButton";
import { Dialog } from "../components-v2/Dialog";
import { isMobileView } from "../utils/screen";
import { isFailure, isOk, isPending } from "../common/common";
import { Alert } from "../components-v2/Alert";
import { FullScreenLayout } from "../components-v2/FullScreenLayout";
import { BackButton, Button } from "../components-v2/Button";
import { Markdown } from "../text/Markdown";
import { SubscriptionName } from "../views/CompanySettings/components/SubscriptionStatus";
import { useLanguage, useTranslation } from "../stores/RootStoreContext";
import { ActivationMethod } from "../common/product";
import { Tag } from "../components-v2/Tag";

export const SubscriptionPaymentDialogNew: FC<{
  controller: SubscriptionTypeSelectionController;
}> = observer(({ controller }) => {
  const { t } = useTranslation();
  return (
    <Dialog fullscreen>
      <FullScreenLayout
        pageHeader={
          <Box justifyContent={"space-between"} padding borderBottom>
            <Box>
              <BackButton onClick={controller.closeDialog} />
            </Box>
            <Box>
              <Button
                label={t("close")}
                variant="secondary"
                size="sm"
                onClick={controller.closeDialog}
              />
            </Box>
          </Box>
        }
        centerPanel={<ProductDescriptions controller={controller} />}
      />
    </Dialog>
  );
});

export const ProductDescriptions: FC<{
  controller: SubscriptionTypeSelectionController;
}> = observer(({ controller }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"} alignItems={isMobileView() ? "start" : "center"} marginTop marginBottom>
      <Box paddingLeft={isMobileView()} textSize={"h4"}>
        {t("payment.subscriptionpopup.intro")}
      </Box>
      <Box paddingLeft={isMobileView()}>
        <SubscriptionName context={controller.subscriptionStatusContext} />
      </Box>
      <PaymentIntervalMethodStatus controller={controller} />
      <ProductLists controller={controller} />
    </VBox>
  );
});

const PaymentIntervalMethodStatus: FC<{ controller: SubscriptionTypeSelectionController }> =
  observer(({ controller }) => {
    const { t } = useTranslation();
    return (
      <Fragment>
        {isFailure(controller.createPaymentUrlResult) && (
          <Alert type={"error"} message={controller.createPaymentUrlResult.error} />
        )}
        {isFailure(controller.createInvoiceResult) && (
          <Alert type={"error"} message={controller.createInvoiceResult.error} />
        )}
        {isOk(controller.createInvoiceResult) && (
          <Alert
            type={"success"}
            message={<>{t("payment.subscriptionpopup.createInvoiceResult.success")}</>}
          />
        )}
        {isOk(controller.upgradeCreditCardSubscriptionResult) && (
          <Alert
            type={"success"}
            message={
              <>{t("payment.subscriptionpopup.upgradeCreditCardSubscriptionResult.success")}</>
            }
          />
        )}
        {isFailure(controller.upgradeCreditCardSubscriptionResult) && (
          <Alert type={"error"} message={controller.upgradeCreditCardSubscriptionResult.error} />
        )}
        <Box gap={"xl"} vertical={isMobileView()} paddingLeft={isMobileView()}>
          <VBox>
            <MRadioButtonGroup model={controller.billIntervalModel} horizontal />
            <Box fg="subtle">{t("payment.subscriptionpopup.save10")}</Box>
          </VBox>
          <VBox>
            <MRadioButtonGroup model={controller.paymentMethodModel} horizontal />
            <Box fg="subtle">{t("payment.subscriptionpopup.weusestripe")}</Box>
          </VBox>
        </Box>
      </Fragment>
    );
  });

const ProductLists: FC<{ controller: SubscriptionTypeSelectionController }> = observer(
  ({ controller }) => {
    const { t } = useTranslation();
    const language = useLanguage();
    return (
      <VBox>
        <Grid
          gap={"lg"}
          cols={1}
          colsLg={controller.listProducts.products.length as GridProps["cols"]}
        >
          {controller.listProducts.products.map((product) => {
            const productDescription = product.productLanguages.find(
              (lang) => lang.language === language,
            );
            if (!productDescription) {
              return null;
            }
            const tier = controller.tierModelsForProduct(product);
            const calculatedPrice = controller.calculateMonthlyPrice(product);
            const requiresCustomization = controller.priceRequiresCustomization(product);
            const validationError = controller.validationErrorForProduct(product);

            const isCurrentproduct =
              product.productId ===
              controller.subscriptionStatusContext.productDescription?.productId;
            return (
              <VBox
                gap={"md"}
                key={product.productId}
                border
                rounded={"lg"}
                padding={"lg"}
                className={
                  "tw-max-w-96 " +
                  (product.useDarkMode ? "bg-gray-x-hard color-gray-xx-soft" : "bg-default")
                }
                justifyContent={"space-between"}
              >
                <VBox marginRight={isMobileView()}>
                  {isCurrentproduct && <Tag color={"info"} label={t("current")} />}
                  <h4>{productDescription.name}</h4>
                  <Box marginRight={isMobileView()}>
                    {productDescription.description && (
                      <Markdown value={productDescription.description} />
                    )}
                  </Box>
                </VBox>
                {tier?.tierModel && (
                  <Box marginRight>
                    <MSelect
                      label={t("payment.subscriptionpopup.shareholders.count")}
                      model={tier.tierModel}
                    />
                  </Box>
                )}
                <VBox justifyContent={"end"}>
                  <VBox paddingBottom>
                    {!requiresCustomization &&
                    product.activationMethod === ActivationMethod.SELF_SERVICE ? (
                      <>
                        <h4>NOK {calculatedPrice === undefined ? " " : calculatedPrice}</h4>
                        <div>{t("settings.subscription.perTimeUnit.month")}</div>
                      </>
                    ) : (
                      <>
                        <h4>{t("payment.subscriptionpopup.tailoredpricing")}</h4>
                        <div>
                          {productDescription.tailoredPriceDescription?.replaceAll(
                            "{{company}}",
                            controller.userContext.companyName,
                          )}
                        </div>
                      </>
                    )}
                  </VBox>
                  {validationError && <Alert type="error" message={validationError} />}
                  {!requiresCustomization &&
                    product.activationMethod === ActivationMethod.SELF_SERVICE && (
                      <Button
                        variant={"primary"}
                        disabled={
                          isOk(controller.createPaymentUrlResult) ||
                          isOk(controller.createInvoiceResult) ||
                          isOk(controller.upgradeCreditCardSubscriptionResult)
                        }
                        loading={
                          isPending(controller.createPaymentUrlResult) ||
                          isPending(controller.createInvoiceResult) ||
                          isPending(controller.upgradeCreditCardSubscriptionResult)
                        }
                        label={t("activate", { name: productDescription.name })}
                        size="sm"
                        onClick={() => controller.buyProduct(product)}
                      />
                    )}

                  {(requiresCustomization ||
                    product.activationMethod === ActivationMethod.REQUEST_OFFER) && (
                    <Button
                      variant={"primary"}
                      label={t("settings.subscription.contactSales")}
                      size="sm"
                      onClick={orderUpgrade({
                        companyName: controller.userContext.companyName,
                        orgNo: controller.userContext.organisationNumber,
                        email: controller.userContext.administratorEmail,
                        name: controller.userContext.administratorName,
                        productName: product.productName,
                      })}
                    />
                  )}
                </VBox>
              </VBox>
            );
          })}
        </Grid>
        <Box
          vertical={isMobileView()}
          paddingX={isMobileView()}
          justifyContent="center"
          gap="md"
          marginTop="lg"
          marginBottom={isMobileView()}
        >
          {[
            [t("payment.subscriptionpopup.functionalities"), "https://dcompany.no/funksjoner/"],
            [t("payment.subscriptionpopup.compare-prices"), "https://dcompany.no/priser/"],
          ].map(([label, url]) => (
            <Button
              key={label}
              label={label}
              variant="secondary"
              size="sm"
              onClick={() => window.open(url, "_blank")}
            />
          ))}
        </Box>
      </VBox>
    );
  },
);

export const ActivateSubscriptionDialog: FC<{
  controller: ActivateSubscriptionController;
}> = observer(({ controller }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("activate-subscription.title", { companyName: controller.context.companyName })}
      onCancel={controller.context.onCloseDialog}
      onSubmit={
        isOk(controller.activateSubscriptionResult) ? undefined : controller.onActivateSubscription
      }
      isSubmitting={isPending(controller.activateSubscriptionResult)}
      isCancelDisabled={false}
      fullscreen={isMobileView()}
      maxWidth={"sm"}
      submitLabel={t("activate-subscription.submit")}
      cancelLabel={isOk(controller.activateSubscriptionResult) ? t("close") : t("cancel")}
    >
      <VBox paddingBottom>
        {!isOk(controller.activateSubscriptionResult) && (
          <>
            <div>{t("activate-subscription.info1")}</div>
            <div>{t("activate-subscription.info2")}</div>
          </>
        )}
      </VBox>
      {isOk(controller.activateSubscriptionResult) && (
        <Alert type={"success"} message={t("activate-subscription.success")} />
      )}
      {isFailure(controller.activateSubscriptionResult) && (
        <Alert type={"error"} message={t("activate-subscription.failure")} />
      )}
    </Dialog>
  );
});

export const ActiveShareholderRegisterSubscriptionDialog: FC<{
  context: ActiveShareholderRegisterSubscriptionContext;
}> = observer(({ context }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("active-shareholder-register.title", { companyName: context.companyName })}
      onCancel={context.onCloseDialog}
      cancelLabel={t("close")}
    >
      <div>{t("active-shareholder-register.body", { companyName: context.companyName })}</div>
    </Dialog>
  );
});
